/**
 * ONE MONTH'S AUDIENCE AS A DONUT - the Audience tab's second way to draw a dimension (the owner,
 * 2026-10-06: "maybe we could add switch like a bar/donut chart types there", then "what is the
 * problem in privacy to show it like a donut % and number"; decision-log row 269).
 *
 * A slice per group and one for the small groups together, in the platform's order, drawn in SVG by
 * the cabinet (no chart library, as the day chart). The words are the legend beside it - a named
 * list, each group with its people and its share - so the drawing itself is hidden from a screen
 * reader and says nothing the list does not. Pointing at a slice or a legend row lights both and puts
 * that group's share and name in the middle; otherwise the middle holds the people the shares are of.
 */
import { Group, Text } from '@mantine/core';
import { useState } from 'react';
import { formatCount } from '../../../features/cabinet/format';
import { useTranslation } from '../../../i18n';
import type { AudienceShares } from './audience-shares';
import { shareText } from './audience-shares';
import styles from './audience-donut.module.css';

const SIZE = 200;
const STROKE = 30;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** The white line between two slices, along the ring. */
const GAP = 2;
const COLOURS = 12;

function colourOf(index: number, merged: boolean): string {
  return merged ? (styles.cMerged ?? '') : (styles[`c${String(index % COLOURS)}`] ?? '');
}

export interface AudienceDonutProps {
  readonly shares: AudienceShares;
  /** The legend's name - the same as the bars' list, so a reader hears one name for one thing. */
  readonly listLabel: string;
  /** A group's size in words ("24 readers"). */
  readonly people: (count: number) => string;
}

export function AudienceDonut({ shares, listLabel, people }: AudienceDonutProps) {
  const { t } = useTranslation('articles');
  const [active, setActive] = useState<number | null>(null);

  let start = 0;
  const arcs = shares.slices.flatMap((slice, index) => {
    if (slice.count === null || slice.count <= 0 || shares.total <= 0) {
      return [];
    }
    const length = (slice.count / shares.total) * CIRCUMFERENCE;
    const arc = { slice, index, length, start };
    start += length;
    return [arc];
  });
  const gap = arcs.length > 1 ? GAP : 0;
  const focused = active === null ? undefined : shares.slices[active];

  return (
    <Group gap="xl" align="center" wrap="wrap" className={styles.donut}>
      <div className={styles.figure}>
        <svg
          className={styles.svg}
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${String(SIZE)} ${String(SIZE)}`}
          aria-hidden="true"
          focusable="false"
        >
          <circle
            className={styles.track}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
          />
          {arcs.map(({ slice, index, length, start: from }) => (
            <circle
              key={slice.key}
              className={`${styles.slice ?? ''} ${colourOf(index, slice.merged)}`}
              data-dim={active !== null && active !== index ? 'true' : undefined}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              strokeDasharray={`${String(Math.max(0.5, length - gap))} ${String(CIRCUMFERENCE)}`}
              strokeDashoffset={-from}
              transform={`rotate(-90 ${String(SIZE / 2)} ${String(SIZE / 2)})`}
              onMouseEnter={() => {
                setActive(index);
              }}
              onMouseLeave={() => {
                setActive(null);
              }}
            />
          ))}
        </svg>
        <div className={styles.centre} aria-hidden="true">
          {focused === undefined || focused.count === null ? (
            <>
              <span className={styles.centreFigure}>{formatCount(shares.total)}</span>
              <span className={styles.centreWords}>{t('stats.audience.donutReaders')}</span>
            </>
          ) : (
            <>
              <span className={styles.centreFigure}>{shareText(focused.count, shares.total)}</span>
              <span className={styles.centreWords}>{focused.label}</span>
            </>
          )}
        </div>
      </div>

      <ul className={styles.legend} aria-label={listLabel}>
        {shares.slices.map((slice, index) => (
          <li
            key={slice.key}
            className={styles.row}
            data-active={active === index ? 'true' : undefined}
            onMouseEnter={() => {
              setActive(index);
            }}
            onMouseLeave={() => {
              setActive(null);
            }}
          >
            <span
              aria-hidden="true"
              className={`${styles.swatch ?? ''} ${colourOf(index, slice.merged)}`}
            />
            <Text
              size="sm"
              className={`${styles.label ?? ''} ${slice.merged ? (styles.merged ?? '') : ''}`}
            >
              {slice.label}
            </Text>
            <Text
              size="sm"
              className={styles.count}
              {...(slice.count === null ? { c: 'dimmed' } : {})}
            >
              {slice.count === null ? t('stats.audience.tooFew') : people(slice.count)}
            </Text>
            {slice.count === null ? (
              <span className={styles.share} />
            ) : (
              <Text size="sm" className={styles.share}>
                {shareText(slice.count, shares.total)}
              </Text>
            )}
          </li>
        ))}
      </ul>
    </Group>
  );
}
